import { create } from 'zustand'
import { User } from '../types'
import { supabase } from '../lib/supabase'
import { authApi, progressApi } from '../api/client'
import { getCompletedPuzzleIds, hasLocalProgressToSync, clearLocalProgress } from '../lib/localProgress'

interface AuthStore {
  user: User | null
  isLoading: boolean
  isAuthenticated: boolean
  _initialized: boolean
  initialize: () => Promise<void>
  loginWithGitHub: () => Promise<void>
  signUp: (email: string, password: string) => Promise<{ needsConfirmation: boolean }>
  signInWithEmail: (email: string, password: string) => Promise<void>
  resetPassword: (email: string) => Promise<void>
  updatePassword: (newPassword: string) => Promise<void>
  logout: () => Promise<void>
}

export const useAuthStore = create<AuthStore>()((set, get) => ({
  user: null,
  isLoading: true,
  isAuthenticated: false,
  _initialized: false,

  initialize: async () => {
    // Prevent multiple initializations
    if (get()._initialized) {
      console.log('[Auth] Already initialized, skipping')
      return
    }
    
    set({ isLoading: true, _initialized: true })
    
    try {
      const { data: { session } } = await supabase.auth.getSession()
      
      if (session?.user) {
        // Fetch profile from database
        const { data: profile } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', session.user.id)
          .single()
        
        if (profile) {
          const user: User = {
            id: session.user.id,
            username: profile.username,
            avatar: profile.avatar,
            email: profile.email || session.user.email,
            totalGamesCompleted: profile.total_games_completed,
            totalTimePlayed: profile.total_time_played,
            easyCompleted: profile.easy_completed,
            mediumCompleted: profile.medium_completed,
            hardCompleted: profile.hard_completed,
            createdAt: profile.created_at,
          }
          
          set({
            user,
            isAuthenticated: true,
            isLoading: false,
          })
        } else {
          set({ isLoading: false })
        }
      } else {
        set({ isLoading: false })
      }
    } catch (error) {
      console.error('Error initializing auth:', error)
      set({ isLoading: false })
    }
    
    // Listen for auth state changes
    supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('[Auth] onAuthStateChange:', event, !!session)
      
      if ((event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED' || event === 'INITIAL_SESSION') && session?.user) {
        // Sync local progress to server on sign in
        if (event === 'SIGNED_IN' && hasLocalProgressToSync()) {
          console.log('[Auth] Syncing local progress to server...')
          try {
            const puzzleIds = getCompletedPuzzleIds()
            const result = await progressApi.syncLocalProgress(puzzleIds)
            console.log('[Auth] Local progress synced:', result)
            // Clear local progress after successful sync
            if (result.results.synced.length > 0 || result.results.skipped.length > 0) {
              clearLocalProgress()
              console.log('[Auth] Local progress cleared after sync')
            }
          } catch (error) {
            console.error('[Auth] Failed to sync local progress:', error)
            // Don't block login on sync failure
          }
        }
        
        // Fetch profile (after sync to get updated stats)
        const { data: profile } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', session.user.id)
          .single()
        
        if (profile) {
          const user: User = {
            id: session.user.id,
            username: profile.username,
            avatar: profile.avatar,
            email: profile.email || session.user.email,
            totalGamesCompleted: profile.total_games_completed,
            totalTimePlayed: profile.total_time_played,
            easyCompleted: profile.easy_completed,
            mediumCompleted: profile.medium_completed,
            hardCompleted: profile.hard_completed,
            createdAt: profile.created_at,
          }
          
          set({
            user,
            isAuthenticated: true,
            isLoading: false,
          })
        }
      } else if (event === 'SIGNED_OUT') {
        set({
          user: null,
          isAuthenticated: false,
          isLoading: false,
        })
      }
    })
  },

  loginWithGitHub: async () => {
    set({ isLoading: true })
    try {
      await authApi.loginWithGitHub()
    } catch (error) {
      console.error('GitHub login error:', error)
      set({ isLoading: false })
      throw error
    }
  },

  signUp: async (email: string, password: string) => {
    set({ isLoading: true })
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/callback`,
        },
      })
      if (error) throw error
      set({ isLoading: false })
      // If session exists, user is auto-confirmed; otherwise needs email confirmation
      return { needsConfirmation: !data.session }
    } catch (error) {
      console.error('Sign up error:', error)
      set({ isLoading: false })
      throw error
    }
  },

  signInWithEmail: async (email: string, password: string) => {
    set({ isLoading: true })
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      })
      if (error) throw error
      // Auth state change listener will handle setting user state
    } catch (error) {
      console.error('Sign in error:', error)
      set({ isLoading: false })
      throw error
    }
  },

  resetPassword: async (email: string) => {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/auth/reset-password`,
      })
      if (error) throw error
    } catch (error) {
      console.error('Reset password error:', error)
      throw error
    }
  },

  updatePassword: async (newPassword: string) => {
    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      })
      if (error) throw error
    } catch (error) {
      console.error('Update password error:', error)
      throw error
    }
  },

  logout: async () => {
    try {
      await supabase.auth.signOut()
      set({ user: null, isAuthenticated: false })
    } catch (error) {
      console.error('Logout error:', error)
      // Still clear local state even if signOut fails
      set({ user: null, isAuthenticated: false })
    }
  },
}))
